/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonVariant, Divider, Intent, Popover, Position, Size, Tooltip } from "@blueprintjs/core"
import { MouseEvent, useEffect, useRef } from "react"
import { ID } from "src/libs/interfaces/layouts"
import { __ } from "src/libs/utilities"
import useScreenSize from "src/modules/pe-basic-module/hooks/useScreenSize"
import { useSyncState } from "src/modules/pe-basic-module/hooks/useSyncState"
import { getLandingContainer } from "../utils/getLandingContainer"
import { PanelData } from "./interface"
import _edge from "./panel-edge.svg"
import PanelHeader from "./PanelHeader"
import "./uix.scss"

/* Protopia Ecosystem component */
export interface IPanelPosition {
    x: number
    y: number
    w: number
    h: number
}

export interface IPanelProps {
   x: number
   y: number
   w: number
   h: number
   isOpen: boolean
   isConsoleOpen?: boolean
   id: ID
   data: PanelData[]
   activeId: ID
   header?: JSX.Element
   isTread?: boolean
   setActiveId: (panelDataId: ID) => void
   onThread?: (panelId: ID, consoleId:ID, bool: boolean, x:number, y: number, w: number, h: number, ) => void
   onClose?: () => void
   onCompact?: ( panelId: ID, isClose: boolean) => void
   onPosition: ( x:number, y: number, w: number, h: number ) => void
}
const Panel = (props: IPanelProps): JSX.Element => {
    return !props.isConsoleOpen && !!props.isTread
        ?
        <PanelCompact {...props} />
        :
        <PanelWide {...props} />
}
const PanelCompact = (props: IPanelProps): JSX.Element => {
    return <div 
        className={`pe-panel-compact`} 
        draggable={ false }
        data-panel-id={ props.id }         
    >
        { 
            props.data.map(panel => {
                return <div key={panel.id}>
                    <Popover
                        position={Position.LEFT_BOTTOM}
                        modifiers={{ 
                            arrow: { enabled: true },
                            flip: { enabled: true },
                            preventOverflow: { enabled: false },
                            offset: { 
                                enabled: true, 
                                options: {
                                    offset: [-45, -2],
                                } 
                            }, 
                        }}
                        content={ 
                            <div className="position-relative" style={{transform: "translateY(0px)"}}>
                                <PanelWide  
                                    { 
                                        ...props 
                                    } 
                                    activeId={panel.id}
                                    w={300}
                                    x={-300}
                                    y={0}
                                    isTread
                                /> 
                            </div>
                        }
                    >
                        <Tooltip 
                            position="left" 
                            content={<div className="small letter-spacing-1">{__(panel.title)}</div> }
                            intent={Intent.PRIMARY} 
                            modifiers={{ 
                                arrow: { enabled: true },
                                flip: { enabled: true },
                                preventOverflow: { enabled: false },
                                offset: { 
                                    enabled: true, 
                                    options: {
                                        offset: [0, -2],
                                    } 
                                }, 
                            }}
                            compact 
                            hoverOpenDelay={600}
                        >
                            <Button variant={ButtonVariant.OUTLINED} size={Size.LARGE} key={panel.id} className="ml-1 mb-1">
                                <div className="py-2">
                                    <img src={panel.icon || ""} alt="" width={16} className="panel-icon" />
                                </div>
                            </Button> 
                        </Tooltip>
                    </Popover>
                </div>
            }) 
        }
        <div className="panel-compact-divider" />
    </div>
}
const PanelWide = (props: IPanelProps): JSX.Element => {
    const sizes                 = useScreenSize()
    const ref                   = useRef(null)
    const refShift              = useRef(null)
    const xShift                = useSyncState<number>( 0 )
    const yShift                = useSyncState<number>( 0 )
    const x                     = useSyncState<number>( props.x < sizes.width  - props.w ? props.x : sizes.width  - props.w )
    const y                     = useSyncState<number>( props.y < sizes.height - props.h ? props.y : sizes.height - props.h )
    const w                     = useSyncState<number>( props.w > sizes.width  ? sizes.width : props.w )
    const h                     = useSyncState<number>( props.h > sizes.height ? sizes.height : props.h  )    
    const isClick               = useSyncState<boolean>( false )
    const isOpen                = useSyncState<boolean>( props.isOpen ) 
    //const width               = useLandingConsoleStore(( state: ILandingConsoleStore) => state.width )
    const landingBound          = useSyncState<number>(0)
 
    const activeData: PanelData = props.data.find(d => d.id === props.activeId) || props.data[0] || {} as PanelData

    useEffect(() => {
        x.set( props.x < sizes.width  - props.w ? props.x : sizes.width  - props.w )
        y.set( props.y < sizes.height - props.h ? props.y : sizes.height - props.h )
        w.set( props.w > sizes.width  ? sizes.width : props.w )
        h.set( props.h > sizes.height ? sizes.height : props.h ) 
        landingBound.set( getLandingContainer().getBoundingClientRect().top ) 
    }, [ props.x, props.y, props.w, props.h ])
 
    const onDragStart = (evt: MouseEvent) => {
        isClick.set( true )  
        const panel = (ref.current! as any)?.getBoundingClientRect() 
        xShift.set( evt.clientX - panel.left ) 
        yShift.set( evt.clientY - panel.top  )
        evt.stopPropagation() 
        
    } 
    const onMouseMove = (evt: MouseEvent) => {   
        if( !!isClick.get() ) {
            const xPos = evt.clientX
            const yPos = evt.clientY      
            const xx = xPos - xShift.get()
            const yy = yPos - yShift.get()  
            if( xx + w.get() < sizes.width && yy + h.get() < sizes.height && xx > 0 && yy > 0 ) {
                x.set( xx )
                y.set( yy )
            } 
        }
        evt.stopPropagation()
        evt.preventDefault() 
    }
    const onPanelStopDrag = (evt: MouseEvent) => { 
        if( !!isClick.get() ) { 
            // создаем новую консоль с данной панелью
            const membrane   = document.querySelector(".pe-panel-membrane")!.getBoundingClientRect()
            if( evt.clientX >= membrane.left && evt.clientX <= membrane.left + membrane.width ) {
                props.onThread && props.onThread(props.id, "-1", true, x.get(), y.get(), w.get(), h.get())  
                return
            }

            // перетаскиваем в консоль
            const consoles      = document.querySelectorAll(".pe-console-panel")
            let _console: string | undefined
            consoles.forEach(console => {
                const bound     = console.getBoundingClientRect()
                if( evt.clientX > bound.left && evt.clientX < bound.left + bound.width ) {
                    _console    = (console.getAttribute("data-console-id") || undefined)
                }
            }) 
            if( !!_console ) {
                props.onThread && props.onThread(props.id, _console, true, x.get(), y.get(), w.get(), h.get())  
            }
            else { 
                // перетаскиваем из консоли на экран
                const xPos = evt.clientX
                const yPos = evt.clientY      
                const xx = xPos - xShift.get()
                const yy = yPos - yShift.get()  
                x.set( xx )
                y.set( yy )  
                props.onThread && props.onThread(props.id, -1, false, xx, yy, w.get(), h.get())
            }
            props.onPosition( x.get(), y.get(), w.get(), h.get() )
            //console.log( x.get(), y.get(), w.get(), h.get() )
        } 
        isClick.set(false) 
        evt.stopPropagation()
        evt.preventDefault() 
    }

    const onShitStartDrag = (evt: MouseEvent) => { 
        xShift.set( evt.clientX ) 
        yShift.set( evt.clientY )
        evt.stopPropagation()
        //evt.preventDefault() 
    } 
    const onShiftDragEnd = (evt: MouseEvent) => {
        props.onPosition( x.get(), y.get(), w.get(), h.get() )
        evt.stopPropagation()
        evt.preventDefault()
    }
    const onShiftDrag = (evt: MouseEvent) => { 
        const panel = (ref.current! as any)
        const xPos = evt.clientX
        const yPos = evt.clientY 
        if(
            xPos - parseInt(panel.style.left) + 6 > 200 &&
            yPos - parseInt(panel.style.top)  + 6 > 26
        ) {
            w.set( xPos - parseInt(panel.style.left) + 6 )
            h.set( yPos - (ref.current! as any).getBoundingClientRect().top - landingBound.get() + 6 )
        }
        evt.stopPropagation()
        evt.preventDefault() 
    }
    

    useEffect(() => { 
        const panel = (ref.current! as any)
        const shifter = (refShift.current! as any)  
        panel.addEventListener("dragstart", onDragStart)
        panel.addEventListener("dragend", onPanelStopDrag)
        panel.addEventListener("drag", onMouseMove)
        if(!!shifter) shifter.addEventListener("dragstart", onShitStartDrag) 
        if(!!shifter) shifter.addEventListener("drag", onShiftDrag) 
        if(!!shifter) shifter.addEventListener("dragend", onShiftDragEnd) 
         
        return () => { 
            panel.removeEventListener("mousedown", onDragStart)
            panel.removeEventListener("dragend", onPanelStopDrag)
            panel.removeEventListener("drag", onMouseMove) 
            if(!!shifter) shifter.removeEventListener("dragstart", onShitStartDrag) 
            if(!!shifter) shifter.removeEventListener("drag", onShiftDrag) 
            if(!!shifter) shifter.removeEventListener("dragend", onShiftDragEnd) 
        }
    }, [])

    const onClose = () => {
        const _isOpen = !isOpen.get()
        isOpen.set( _isOpen )
        if(props.onCompact) {
            props.onCompact( props.id, !_isOpen )
        }
    }
 
    return <div 
        className={`pe-panel-container ${ isClick.get() ? "active" : "" }`} draggable
        data-panel-id={ props.id } 
        ref={ref}
        style={{ 
            top:    y.get(), 
            left:   x.get(), 
            width:  w.get(), 
            height: isOpen.get() ? h.get() : 26
        }}
    >
        <PanelHeader 
            isOpen={ isOpen.get() } 
            setIsOpen={ onClose } 
            onClose={ props.onClose } 
            data={ props.data }
            activeData={ activeData }
            setActiveId={ props.setActiveId }
            title={ activeData.title } 
            isTread={ !!props.isTread } 
            icon={ activeData.icon || "" } 
            addHeader={props.header} 
        />
        {
            isOpen.get() && 
            <div className="pe-panel-content w-100 h-100">
                { activeData.data }
            </div> 
        }
        <div className='pe-panel-shifter' ref={refShift} >
            <img src={_edge} height={12} alt="" />
        </div>
    </div>
}
export default Panel